import React from "react";
import { GraduationCap, MapPin, Briefcase, Github, Youtube, Linkedin, MessageCircle, Music2 } from "lucide-react";
import { useLightbox } from "../Lightbox";
import { Overline } from "../common/Panel";

const DIAG_IMAGES = [
  { src: "/assets/hw-real-1.jpg", title: "Enclosure Overview",        desc: "Open build with LCD, load panel on desk" },
  { src: "/assets/hw-real-2.jpg", title: "Internal Wiring, Side A",  desc: "ESP32 on breadboard, fan and all sensors wired" },
  { src: "/assets/hw-real-3.jpg", title: "ESP32 Powered Up",          desc: "Blue and red LEDs, full breadboard connections" },
  { src: "/assets/hw-real-4.jpg", title: "Full Test Setup",           desc: "Enclosure, LTE router, load board and camera" },
];

const STACK = [
  "ESP32-WROOM-32U", "3× PZEM-004T v3.0", "3× Split-core CT (100A)",
  "DHT11 (GPIO 17)", "MQ-135 Gas Sensor (GPIO 34)", "HC-SR501 PIR (GPIO 4)",
  "IRLZ44N MOSFET × 2", "12V Boost Converter", "5V Active Buzzer (GPIO 18)",
  "20×4 I²C LCD (0x27)", "TTGO T-Camera v1.6 (OV2640)", "HiveMQ Cloud MQTT", "React", "Cloudflare Pages",
  "Grafana Cloud", "ThingSpeak",
];

const SOCIALS = [
  [Github,        "GitHub",   "https://github.com/henockhnk092-dot"],
  [Youtube,       "YouTube",  "https://youtube.com/@HNK2005"],
  [Music2,        "TikTok",   "https://tiktok.com/@codingfever"],
  [Linkedin,      "LinkedIn", "https://linkedin.com"],
  [MessageCircle, "WhatsApp", "https://wa.me/27754531797"],
];

export default function AboutTab() {
  const lb = useLightbox();

  return (
    <div className="space-y-8">
      <div>
        <Overline>About</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">EnergyLens</h2>
      </div>

      <div className="prose-none text-[var(--t2)] leading-relaxed space-y-4 text-sm">
        <p>
          <span className="text-[var(--t1)] font-semibold">The problem: </span>
          Many shared buildings have no real-time three-phase IoT monitoring for voltage, current, power, power factor, frequency, and energy. Commercial BMS solutions cost over R500,000, making them unaffordable for small landlords or building managers. Monthly utility bills offer no visibility into peak consumption, phase imbalances, or power factor issues.
        </p>
        <p>
          <span className="text-[var(--t1)] font-semibold">Original specification: </span>
          Design and deploy a low-cost IoT energy monitoring system with 4 objectives: build a 3-phase monitoring node with ESP32 and PZEM-004T, transmit to ThingSpeak at 15-second intervals, develop a Grafana/ThingSpeak dashboard, and collect 4 weeks of continuous data for load profile analysis. The original electronics BOM was ~R2,595. After extending the project with additional sensors (DHT11, MQ-135, HC-SR501 PIR), actuators (IRLZ44N MOSFETs × 2, TIP120 buzzer driver), custom enclosure build, and AC load setup, the total investment increased, but still represents a &gt;95% cost reduction vs commercial BMS systems at R500,000+.
        </p>
        <p>
          <span className="text-[var(--t1)] font-semibold">What was built, beyond the specification: </span>
          The project was expanded from a passive 4-objective monitoring dashboard into a comprehensive IoT command center with 7 objectives. Data transmission was upgraded from 15-second ThingSpeak polling to 2-second HiveMQ Cloud MQTT streaming. The basic dashboard was replaced with a full React web application featuring user authentication, personalization, and mobile-responsive design deployed on Cloudflare Pages.
        </p>
        <p>
          Environmental monitoring was added: a DHT11 sensor (GPIO 17) drives automatic PWM fan control via an IRLZ44N MOSFET (GPIO 25, 25Hz LEDC channel 2) through a 12V boost converter, with 4 automatic threshold levels and an 800ms kickstart routine. An MQ-135 gas sensor (GPIO 34, 10kΩ+22kΩ voltage divider) classifies enclosure air quality as GOOD, POOR, or DANGER with buzzer alerts. Remote hardware control is implemented over MQTT: fan speed, light hub (IRLZ44N GPIO 32, 1kHz LEDC), and buzzer, accessible from the cloud dashboard. A PIR sensor (HC-SR501, GPIO 4) auto-dims the light hub after 5 seconds of no motion, bypassed when the user manually sets the light via MQTT. Data flows to both Grafana Cloud and ThingSpeak dual-channel (Ch-A: V/I/W per phase for L1 and L2; Ch-B: W_L3, PF, Hz and kWh_Total).
        </p>
      </div>

      {/* Tech stack */}
      <div>
        <Overline>Technology Stack</Overline>
        <div className="flex flex-wrap gap-2 mt-3">
          {STACK.map((s) => (
            <span key={s} className="px-3 py-1.5 rounded-full text-xs font-mono bg-[var(--surface2)] border border-[var(--bd)]">{s}</span>
          ))}
        </div>
      </div>

      {/* Component kit */}
      <div>
        <Overline>Inside the Kit</Overline>
        <div
          className="mt-3 rounded-2xl overflow-hidden border border-[var(--bd)] bg-black cursor-pointer"
          onClick={() => lb.open([{ src: "/assets/kit-flatlay.jpg", title: "Component Kit, Flatlay", desc: "ESP32, 3× PZEM-004T, LCD, CTs, PSU and breadboard" }], 0)}
        >
          <img src="/assets/kit-flatlay.jpg" alt="Full component kit" className="w-full h-auto" />
        </div>
        <p className="text-xs text-[var(--t2)] mt-2 font-mono">
          ESP32-WROOM-32U, 3× PZEM-004T v3.0, 3× split-core CT, DHT11, MQ-135, HC-SR501 PIR, 20×4 I²C LCD, 5V/3A PSU
        </p>
      </div>

      {/* Engineering docs */}
      <div>
        <Overline>Engineering Docs</Overline>
        <h3 className="font-heading font-bold text-xl tracking-tight mt-1">System architecture & documentation</h3>
        <p className="text-sm text-[var(--t2)] mt-2 mb-5">The full IoT stack, from CT clamps to the cloud. Click any diagram to view full size.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DIAG_IMAGES.map(({ src, title, desc }, i) => (
            <div key={title} onClick={() => lb.open(DIAG_IMAGES, i)}
              className="group rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors cursor-pointer">
              <div className="aspect-[4/3] overflow-hidden bg-white">
                <img src={src} alt={title} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
              </div>
              <div className="p-4">
                <h4 className="font-heading font-bold text-sm">{title}</h4>
                <p className="text-xs text-[var(--t2)] mt-0.5 font-mono">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Developer card */}
      <div className="rounded-2xl border border-[var(--bd)] overflow-hidden bg-[var(--surface)] grid md:grid-cols-[320px_1fr]">
        <img src="/assets/profile-energylens.jpeg" alt="EnergyLens project" className="w-full h-auto object-cover" />
        <div className="p-6 sm:p-8">
          <h3 className="font-heading font-black text-2xl">EnergyLens Project</h3>
          <p className="text-[var(--accent)] font-mono text-sm mt-1">Open IoT Project, Cape Town, South Africa, 2026</p>
          <p className="text-sm text-[var(--t2)] mt-3 leading-relaxed">Open-source IoT project building affordable energy monitoring for shared living spaces. Built on ESP32, PZEM-004T sensors and a full cloud stack.</p>
          <div className="mt-4 space-y-2 text-sm text-[var(--t2)] font-mono">
            <div className="flex items-center gap-2"><MapPin size={14} /> Cape Town, South Africa</div>
          </div>
          <div className="flex flex-wrap gap-2 mt-6">
            {SOCIALS.map(([Icon, label, href]) => (
              <a key={label} href={href} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[var(--bd)] text-sm hover:border-[var(--accent)] transition-colors">
                <Icon size={15} className="text-[var(--accent)]" /> {label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {lb.node}
    </div>
  );
}
